import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";
import SignInModal from "./SignInModal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const { toggleLang, isAr, t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  const navItems = [
    { id: "features", en: "Features", ar: "المميزات" },
    { id: "pricing", en: "Pricing", ar: "الأسعار" },
    { id: "contact-form", en: "Contact", ar: "تواصل معنا" },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/96 backdrop-blur-md border-b border-line"
            : "bg-transparent"
        }`}
        dir={isAr ? "rtl" : "ltr"}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">

          {/* Logo */}
          <button
            className={`flex items-center gap-3 cursor-pointer ${isAr ? "flex-row-reverse" : ""}`}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="w-9 h-9 rounded-xl bg-gold flex items-center justify-center flex-shrink-0">
              <i className="ri-door-open-line text-white text-lg"></i>
            </div>
            <div className={isAr ? "text-right" : "text-left"}>
              <div className="flex items-center gap-1">
                <span className={`font-tajawal font-bold text-lg leading-none ${scrolled ? "text-ink" : "text-white"}`}>
                  ضيافة
                </span>
                <span className={`font-inter font-bold text-lg leading-none ${scrolled ? "text-ink" : "text-white"}`}>
                  Diyafa
                </span>
              </div>
            </div>
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`text-sm font-medium transition-colors cursor-pointer hover:text-gold whitespace-nowrap ${
                  scrolled ? "text-ink-soft" : "text-white/85"
                } ${isAr ? "font-tajawal" : ""}`}
              >
                {t(item.en, item.ar)}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2.5">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer border ${
                scrolled
                  ? "text-ink-soft border-line-strong hover:bg-cream"
                  : "text-white/80 border-white/20 hover:bg-white/10"
              }`}
            >
              <i className="ri-translate-2 text-sm"></i>
              {isAr ? "English" : "عربي"}
            </button>
            <button
              onClick={() => setSignInOpen(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                scrolled ? "text-ink hover:bg-cream" : "text-white hover:bg-white/10"
              } ${isAr ? "font-tajawal" : ""}`}
            >
              {t("Sign In", "تسجيل الدخول")}
            </button>
            <button
              onClick={() => navigate("/register")}
              className={`px-5 py-2 rounded-xl text-sm font-semibold text-ink bg-gold transition-all cursor-pointer whitespace-nowrap hover:bg-gold-dark ${isAr ? "font-tajawal" : ""}`}
            >
              {t("Start Free Trial", "ابدأ مجاناً")}
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${scrolled ? "text-ink" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-line px-4 py-4 flex flex-col gap-3" dir={isAr ? "rtl" : "ltr"}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`text-sm font-medium text-ink-soft hover:text-gold cursor-pointer py-1.5 ${isAr ? "text-right font-tajawal" : "text-left"}`}
              >
                {t(item.en, item.ar)}
              </button>
            ))}
            <button
              onClick={toggleLang}
              className={`flex items-center gap-2 text-sm font-medium text-gold cursor-pointer py-1 ${isAr ? "flex-row-reverse" : ""}`}
            >
              <i className="ri-translate-2"></i>
              {isAr ? "Switch to English" : "التبديل إلى العربية"}
            </button>
            <div className="flex gap-2 pt-2 border-t border-line">
              <button
                onClick={() => { setSignInOpen(true); setMobileOpen(false); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium text-ink border border-line-strong cursor-pointer ${isAr ? "font-tajawal" : ""}`}
              >
                {t("Sign In", "تسجيل الدخول")}
              </button>
              <button
                onClick={() => { navigate("/register"); setMobileOpen(false); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-ink bg-gold cursor-pointer ${isAr ? "font-tajawal" : ""}`}
              >
                {t("Start Free Trial", "ابدأ مجاناً")}
              </button>
            </div>
          </div>
        )}
      </nav>

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
}
